import React, { useState, useEffect, useCallback } from "react";
import * as echarts from "echarts";
import NavigationSidebar from "../components/Navigationsidebar/NavigationSidebar";
import Header from "../components/Header";
import FilterSidebar from "../components/FilterSidebar/FilterSidebar";
import ApexCharts from "react-apexcharts";
import rawData from "../current-survey-openText.json";
import Select from "react-select";
import { FILTER_CONFIG } from "../config/filterconfig";
import "./D11_Open_Text_Heatmap.css";

const qNumOptions = [
  { value: "MQ0305", label: "What Makes this Company a Great Place to Work" },
  {
    value: "MQ0156",
    label: "What Would Make this Place a Better Place to Work",
  },
  {
    value: "MQ0160",
    label: "What Would you Like to Tell the Senior Leadership Team",
  },
];

const D11_Open_Text_Heatmap = () => {
  const [heatmapData, setHeatmapData] = useState([]);
  const [xAxisOptions, setXAxisOptions] = useState([]);
  const [selectedXAxis, setSelectedXAxis] = useState(null);
  const [selectedQNum, setSelectedQNum] = useState(qNumOptions[0]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [filterCriteria, setFilterCriteria] = useState({});

  useEffect(() => {
    const xAxisDynamicOptions = FILTER_CONFIG.map((config) => ({
      value: config.jsonKey,
      label: config.displayName,
    }));

    setXAxisOptions(xAxisDynamicOptions);

    const defaultXAxis = xAxisDynamicOptions.find(
      (option) => option.value === "demographic_category_04"
    );
    setSelectedXAxis(defaultXAxis || xAxisDynamicOptions[0]);

    const generateFilterData = () => {
      const options = {};
      FILTER_CONFIG.forEach(({ jsonKey, sortOrder }) => {
        let uniqueValues = [
          ...new Set(rawData.map((item) => item[jsonKey])),
        ].filter(Boolean);

        if (sortOrder) {
          uniqueValues = uniqueValues.sort(
            (a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b)
          );
        } else {
          uniqueValues = uniqueValues.sort((a, b) => a.localeCompare(b));
        }

        options[jsonKey] = uniqueValues;
      });
      setFilterData(options);
    };

    generateFilterData();
  }, [rawData]);

  useEffect(() => {
    if (!selectedXAxis || !selectedQNum) return;

    const allXAxisValues = [
      ...new Set(rawData.map((item) => item[selectedXAxis.value])),
    ].filter(Boolean);

    const countData = {};

    rawData
      .filter(
        (record) =>
          record?.q_num === selectedQNum.value &&
          Object.keys(filterCriteria).every((key) => {
            const filterValues = filterCriteria[key];
            const itemValue = record[key];
            return (
              filterValues?.length === 0 || filterValues?.includes(itemValue)
            );
          })
      )
      .forEach((record) => {
        const xValue = record[selectedXAxis?.value];
        if (!xValue) return;

        for (let i = 1; i <= 8; i++) {
          const theme = record[`theme_0${i}`];
          if (!theme || theme.toLowerCase() === "na") continue;

          const key = `${xValue}|${theme}`;
          if (!countData[key]) {
            countData[key] = 0;
          }
          countData[key] += 1;
        }
      });

    const transformedData = [];

    allXAxisValues.forEach((xValue) => {
      const themesForXValue = Object.keys(countData)
        .filter((key) => key.startsWith(`${xValue}|`))
        .map((key) => {
          const theme = key.split("|")[1];
          return { theme, count: countData[key] };
        });

      themesForXValue.forEach(({ theme, count }) => {
        let series = transformedData.find((s) => s.name === theme);
        if (!series) {
          series = { name: theme, data: [] };
          transformedData.push(series);
        }
        series.data.push({ x: xValue, y: count });
      });

      const existingThemes = themesForXValue.map(({ theme }) => theme);
      const allThemes = [
        ...new Set(
          rawData.flatMap((item) =>
            Array.from({ length: 8 }, (_, i) => item[`theme_0${i + 1}`]).filter(
              Boolean
            )
          )
        ),
      ];

      allThemes.forEach((theme) => {
        if (!existingThemes.includes(theme)) {
          let series = transformedData.find((s) => s.name === theme);
          if (!series) {
            series = { name: theme, data: [] };
            transformedData.push(series);
          }
          series.data.push({ x: xValue, y: 0 });
        }
      });
    });

    // Filter out themes where all counts are 0
    const filteredData = transformedData.filter((series) =>
      series.data.some((dataPoint) => dataPoint.y > 0)
    );

    setHeatmapData(filteredData);
  }, [selectedXAxis, selectedQNum, filterCriteria, rawData]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prevState) => !prevState);
  }, []);

  const toggleFilterSidebar = useCallback(() => {
    setIsFilterSidebarOpen((prevState) => !prevState);
  }, []);

  // Add refresh page functionality
  const refreshPage = useCallback(() => {
    window.location.reload();
  }, []);

  const chartOptions = {
    chart: {
      type: "heatmap",
      height: 600,
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => val,
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.6,
        colorScale: {
          ranges: [
            { from: 0, to: 0, color: "#e0e0e0" }, // Slightly darker gray for zero
            { from: 1, to: 5, color: "#a8d8f0" }, // Slightly darker light blue
            { from: 6, to: 10, color: "#79c4eb" },
            { from: 11, to: 15, color: "#4fb0e6" },
            { from: 16, to: 20, color: "#279cdc" },
            { from: 21, to: 30, color: "#1a86c5" },
            { from: 31, to: 40, color: "#106fae" },
            { from: 41, to: 50, color: "#0a5897" },
            { from: 51, to: 200, color: "#043f78" },
          ],
        },
      },
    },
    xaxis: {
      title: { text: selectedXAxis?.label || "X-Axis" },
      categories: (() => {
        if (!selectedXAxis) return [];
        const currentFilterConfig = FILTER_CONFIG.find(
          (config) => config.jsonKey === selectedXAxis.value
        );
        const sortOrder = currentFilterConfig?.sortOrder || [];

        const uniqueCategories = [
          ...new Set(heatmapData.flatMap((s) => s.data.map((d) => d.x))),
        ];

        return uniqueCategories.sort((a, b) => {
          if (sortOrder.length > 0) {
            return sortOrder.indexOf(a) - sortOrder.indexOf(b);
          }
          return a.localeCompare(b);
        });
      })(),
      labels: {
        rotate: -45,
        style: { fontSize: "10px" },
      },
    },
    yaxis: {
      title: { text: "Themes" },
      categories: heatmapData.map((series) => series.name),
    },
    title: {
      text: `Heatmap of Themes for ${selectedQNum?.label} Across ${selectedXAxis?.label}`,
    },
  };

  return (
    <div className="D11_Open_Text_Heatmap-dashboard-container">
      <Header
        toggleSidebar={toggleSidebar}
        toggleFilterSidebar={toggleFilterSidebar}
        refreshPage={refreshPage}
      />
      <NavigationSidebar isOpen={isSidebarOpen} />
      <FilterSidebar
        isOpen={isFilterSidebarOpen}
        filterOptions={filterData}
        setFilterCriteria={setFilterCriteria}
      />
      <div className="D11_Open_Text_Heatmap-main-content">
        <div className="D11_Open_Text_Heatmap-filters">
          <div className="D11_Open_Text_Heatmap-dropdown">
            <label>X-Axis</label>
            <Select
              options={xAxisOptions}
              value={selectedXAxis}
              onChange={(value) => setSelectedXAxis(value)}
            />
          </div>
          <div className="D11_Open_Text_Heatmap-dropdown">
            <label>Question</label>
            <Select
              options={qNumOptions}
              value={selectedQNum}
              onChange={(value) => setSelectedQNum(value)}
            />
          </div>
        </div>
        <div className="D11_Open_Text_Heatmap-charts-container">
          <ApexCharts
            options={chartOptions}
            series={heatmapData}
            type="heatmap"
          />
        </div>
      </div>
    </div>
  );
};

export default D11_Open_Text_Heatmap;